import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { TbTrash } from 'react-icons/tb';
import SelectOptionsEditor from '../components/SelectOptionsEditor';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import {
  CUSTOMER_JOURNEY_TASK_STATE_COLOR_OPTIONS,
  FIELD_TYPE_OPTIONS,
  PRODUCT_STATUS_LABELS,
  ProductSwatch,
  buildCustomerJourneyPayload,
  buildInboxConfigPayload,
  buildProductPayload,
  createEmptyCustomerJourneyField,
  createEmptyCustomerJourneyEvent,
  createEmptyCustomerJourneyStep,
  createEmptyCustomerJourneyTask,
  createCustomerJourneyStatus,
  createCustomerJourneyTaskState,
  createDraftFromProduct,
  createEmptyDraft,
  createEmptyInboxField,
  mapAssignedBot,
  mapProductItem,
  normalizeColor,
  normalizeCustomerJourneyStatuses,
  normalizeCustomerJourneyTaskStates,
  normalizeCustomerJourneyPriority,
  validateDraft,
} from './productConfigUtils';
import './ProductsPage.css';
import './AttributeEditor.css';
import './BotAssignmentPills.css';
import './ProductEditPage.css';

const PRODUCTS_PATH = '/konfigurasjon/produkter';
let productAttributeClientKeyCounter = 0;
let customerJourneyClientKeyCounter = 0;

const CUSTOMER_JOURNEY_TASK_INPUT_TYPE_OPTIONS = [
  { value: 'toggle', label: 'Valg' },
  { value: 'checkmark', label: 'Avkryssing' },
  { value: 'comment', label: 'Kommentar' },
  { value: 'date', label: 'Dato' },
];

const RequiredPillToggle = ({
  checked,
  disabled = false,
  onChange,
}) => (
  <button
    type="button"
    className={`customer-journey-required-badge${checked ? ' is-required' : ' is-optional'}`}
    onClick={() => onChange(!checked)}
    disabled={disabled}
    aria-pressed={checked}
  >
    {checked ? 'Påkrevd' : 'Valgfri'}
  </button>
);

const createAttributeClientKey = () => {
  productAttributeClientKeyCounter += 1;
  return `product-attribute-${Date.now()}-${productAttributeClientKeyCounter}`;
};

const createCustomerJourneyClientKey = () => {
  customerJourneyClientKeyCounter += 1;
  return `customer-journey-${Date.now()}-${customerJourneyClientKeyCounter}`;
};

const getAttributeEntryKey = (field, index) => (
  field?.clientKey || `product-attribute-${field?.key || index}`
);

const getJourneyStepEntryKey = (step, index) => (
  step?.clientKey || `customer-journey-step-${step?.id || step?.key || index}`
);

const getJourneyFieldEntryKey = (field, index) => (
  field?.clientKey || `customer-journey-field-${field?.id || field?.key || index}`
);

const getJourneyTaskEntryKey = (task, index) => (
  task?.clientKey || `customer-journey-task-${task?.id || task?.key || index}`
);

const getJourneyEventEntryKey = (event, index) => (
  event?.clientKey || `customer-journey-event-${event?.id || event?.key || index}`
);

const getJourneyEventTaskEntryKey = (task, index) => (
  task?.clientKey || `customer-journey-event-task-${task?.id || task?.key || index}`
);

const getJourneyStatusEntryKey = (status, index) => (
  status?.clientKey || `customer-journey-status-${status?.key || index}`
);

const getJourneyContentSortOrder = (item, fallback = 0) => {
  const raw = Number(item?.sortOrder ?? item?.sort_order);
  return Number.isFinite(raw) ? raw : fallback;
};

const getJourneyStepLogFields = (step) => (
  Array.isArray(step?.logFields)
    ? step.logFields
    : Array.isArray(step?.fields)
      ? step.fields
      : []
);

const getJourneyStepContentItems = (step) => {
  const tasks = Array.isArray(step?.tasks) ? step.tasks : [];
  const events = Array.isArray(step?.events) ? step.events : [];
  const logFields = getJourneyStepLogFields(step);
  const logSortOrder = logFields.length > 0
    ? Math.min(...logFields.map((field, fieldIndex) => getJourneyContentSortOrder(field, tasks.length + events.length + fieldIndex)))
    : null;
  return [
    ...tasks.map((item, itemIndex) => ({
      kind: 'task',
      item,
      itemIndex,
      sortOrder: getJourneyContentSortOrder(item, itemIndex),
      typeRank: 0,
    })),
    ...events.map((item, itemIndex) => ({
      kind: 'event',
      item,
      itemIndex,
      sortOrder: getJourneyContentSortOrder(item, tasks.length + itemIndex),
      typeRank: 1,
    })),
    ...(logFields.length > 0 ? [{
      kind: 'log',
      item: {
        id: 'log_fields',
        label: 'Loggfelt',
        fields: sortJourneyContentArray(logFields),
      },
      itemIndex: 0,
      sortOrder: logSortOrder,
      typeRank: 2,
    }] : []),
  ].sort((a, b) => (
    a.sortOrder - b.sortOrder
    || a.typeRank - b.typeRank
    || a.itemIndex - b.itemIndex
  ));
};

const getJourneyStepTaskCount = (step) => (
  (Array.isArray(step?.tasks)
    ? step.tasks.reduce((sum, task) => (
      sum + (Array.isArray(task?.tasks) && task.tasks.length > 0 ? task.tasks.length : 1)
    ), 0)
    : 0)
  + (Array.isArray(step?.events)
    ? step.events.reduce((sum, event) => sum + (Array.isArray(event?.tasks) ? event.tasks.length : 0), 0)
    : 0)
);

const sortJourneyContentArray = (items) => [...items].sort((a, b) => (
  getJourneyContentSortOrder(a) - getJourneyContentSortOrder(b)
));

const applyJourneyStepContentOrder = (step, orderedItems = getJourneyStepContentItems(step)) => {
  const tasks = (step.tasks || []).map((task) => ({ ...task }));
  const events = (step.events || []).map((eventItem) => ({ ...eventItem }));
  const logFields = getJourneyStepLogFields(step).map((field) => ({ ...field }));
  orderedItems.forEach((entry, sortOrder) => {
    if (entry.kind === 'task' && tasks[entry.itemIndex]) {
      tasks[entry.itemIndex] = { ...tasks[entry.itemIndex], sortOrder };
    }
    if (entry.kind === 'event' && events[entry.itemIndex]) {
      events[entry.itemIndex] = { ...events[entry.itemIndex], sortOrder };
    }
    if (entry.kind === 'log') {
      const sortedFields = sortJourneyContentArray(logFields);
      sortedFields.forEach((field, fieldIndex) => {
        const originalIndex = logFields.findIndex((candidate) => (
          (candidate.clientKey && candidate.clientKey === field.clientKey)
          || (candidate.id && candidate.id === field.id)
          || candidate === field
        ));
        if (originalIndex >= 0) {
          logFields[originalIndex] = { ...logFields[originalIndex], sortOrder: sortOrder + (fieldIndex / 1000) };
        }
      });
    }
  });
  const orderedLogFields = sortJourneyContentArray(logFields);
  return {
    ...step,
    tasks: sortJourneyContentArray(tasks),
    events: sortJourneyContentArray(events),
    logFields: orderedLogFields,
    fields: orderedLogFields,
  };
};

const normalizeJourneyTaskStatesForEdit = (taskStates) => (
  normalizeCustomerJourneyTaskStates(taskStates, { preserveEmptyLabels: true })
);

const createProductInboxFieldDraft = () => ({
  ...createEmptyInboxField(),
  clientKey: createAttributeClientKey(),
});

const createCustomerJourneyStepDraft = (index = 0) => ({
  ...createEmptyCustomerJourneyStep(),
  sortOrder: index,
  clientKey: createCustomerJourneyClientKey(),
});

const createCustomerJourneyFieldDraft = () => ({
  ...createEmptyCustomerJourneyField(),
  clientKey: createCustomerJourneyClientKey(),
});

const createCustomerJourneyTaskDraft = (index = 0) => ({
  ...createEmptyCustomerJourneyTask(index),
  clientKey: createCustomerJourneyClientKey(),
});

const createCustomerJourneyTaskFieldDraft = (index = 0) => ({
  ...createCustomerJourneyTaskDraft(index),
  label: index === 0 ? 'Nytt felt' : '',
});

const createPromotedCustomerJourneyTaskField = (task, index = 0, fallbackKey = '') => {
  const fieldDraft = createCustomerJourneyTaskFieldDraft(index);
  return {
    ...task,
    id: fieldDraft.id,
    key: fieldDraft.id,
    sortOrder: index,
    clientKey: `${fallbackKey || task.clientKey || task.id || task.key || 'task'}-field-${index}`,
    tasks: [],
  };
};

const createCustomerJourneyTaskGroupChild = (task, index = 0, fallbackKey = '') => ({
  ...task,
  sortOrder: index,
  clientKey: task.clientKey || `${fallbackKey || task.id || task.key || 'task'}-child-${index}`,
  tasks: [],
});

const normalizeCustomerJourneyTaskGroupForEdit = (task, taskIndex, fallbackKey = '') => {
  const childTasks = Array.isArray(task.tasks) ? task.tasks : [];
  if (childTasks.length > 0) {
    return {
      ...task,
      taskStates: [],
      tasks: childTasks.map((childTask, childTaskIndex) => createCustomerJourneyTaskGroupChild(
        childTask,
        childTaskIndex,
        `${fallbackKey || task.clientKey || task.id || task.key || taskIndex}-${childTask.id || childTask.key || childTaskIndex}`,
      )),
    };
  }
  const childTask = createCustomerJourneyTaskGroupChild(task, 0, fallbackKey || task.clientKey || task.id || task.key || taskIndex);
  return {
    ...task,
    inputType: 'toggle',
    taskStates: [],
    tasks: [childTask],
  };
};

const createCustomerJourneyEventDraft = (index = 0) => ({
  ...createEmptyCustomerJourneyEvent(index),
  clientKey: createCustomerJourneyClientKey(),
});

const createCustomerJourneyStatusDraft = (index = 0) => ({
  ...createCustomerJourneyStatus(index),
  clientKey: createCustomerJourneyClientKey(),
});

const attachAttributeClientKeys = (nextDraft) => ({
  ...nextDraft,
  inboxConfig: {
    ...nextDraft.inboxConfig,
    fields: (nextDraft.inboxConfig?.fields || []).map((field, index) => ({
      ...field,
      clientKey: field.clientKey || `product-attribute-${field.key || index}`,
    })),
  },
  customerJourney: {
    ...nextDraft.customerJourney,
    steps: (nextDraft.customerJourney?.steps || []).map((step, index) => {
      const keyedStep = {
        ...step,
        sortOrder: index,
        clientKey: step.clientKey || `customer-journey-step-${step.id || step.key || index}`,
        tasks: (step.tasks || []).map((task, taskIndex) => {
          const taskKey = `customer-journey-task-${step.id || step.key || index}-${task.id || task.key || taskIndex}`;
          return normalizeCustomerJourneyTaskGroupForEdit({
            ...task,
            sortOrder: getJourneyContentSortOrder(task, taskIndex),
            clientKey: task.clientKey || taskKey,
          }, taskIndex, taskKey);
        }),
        events: (step.events || []).map((event, eventIndex) => ({
          ...event,
          sortOrder: getJourneyContentSortOrder(event, (step.tasks || []).length + eventIndex),
          clientKey: event.clientKey || `customer-journey-event-${step.id || step.key || index}-${event.id || event.key || eventIndex}`,
          tasks: (event.tasks || []).map((task, taskIndex) => {
            const taskKey = `customer-journey-event-task-${step.id || step.key || index}-${event.id || event.key || eventIndex}-${task.id || task.key || taskIndex}`;
            return normalizeCustomerJourneyTaskGroupForEdit({
              ...task,
              sortOrder: getJourneyContentSortOrder(task, taskIndex),
              clientKey: task.clientKey || taskKey,
            }, taskIndex, taskKey);
          }),
        })),
        logFields: (step.logFields || step.fields || []).map((field, fieldIndex) => ({
          ...field,
          sortOrder: getJourneyContentSortOrder(field, (step.tasks || []).length + (step.events || []).length + fieldIndex),
          clientKey: field.clientKey || `customer-journey-field-${step.id || step.key || index}-${field.id || field.key || fieldIndex}`,
        })),
        fields: (step.logFields || step.fields || []).map((field, fieldIndex) => ({
          ...field,
          sortOrder: getJourneyContentSortOrder(field, (step.tasks || []).length + (step.events || []).length + fieldIndex),
          clientKey: field.clientKey || `customer-journey-field-${step.id || step.key || index}-${field.id || field.key || fieldIndex}`,
        })),
      };
      return applyJourneyStepContentOrder(keyedStep);
    }),
    statuses: normalizeCustomerJourneyStatuses(nextDraft.customerJourney?.statuses || []).map((status, index) => ({
      ...status,
      clientKey: status.clientKey || `customer-journey-status-${status.key || index}`,
    })),
  },
});

export default function ProductEditPage() {
  const { selectedCompanyId, permissions } = useAuth();
  const { productId } = useParams();
  const navigate = useNavigate();
  const isNewProduct = !productId;
  const numericProductId = Number(productId);
  const hasValidProductId = Number.isInteger(numericProductId) && numericProductId > 0;

  const [product, setProduct] = useState(null);
  const [bots, setBots] = useState([]);
  const [draft, setDraft] = useState(() => createEmptyDraft());
  const [savedDraft, setSavedDraft] = useState(() => createEmptyDraft());
  const [expandedAttributes, setExpandedAttributes] = useState({});
  const [expandedJourneySteps, setExpandedJourneySteps] = useState({});
  const [expandedJourneyStatuses, setExpandedJourneyStatuses] = useState({});
  const [openJourneyContentMenuKey, setOpenJourneyContentMenuKey] = useState(null);
  const [confirmingAttributeDelete, setConfirmingAttributeDelete] = useState(null);
  const [confirmingJourneyDelete, setConfirmingJourneyDelete] = useState(null);
  const [confirmingStatusDelete, setConfirmingStatusDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const canView = Boolean(permissions?.canViewProducts || permissions?.canViewBots);
  const canEdit = Boolean(permissions?.canManageProducts || permissions?.canManageBots);

  const loadProductContext = useCallback(async () => {
    if (!selectedCompanyId || !canView) {
      setLoading(false);
      return;
    }
    if (!isNewProduct && !hasValidProductId) {
      setProduct(null);
      const nextDraft = attachAttributeClientKeys(createEmptyDraft());
      setDraft(nextDraft);
      setSavedDraft(nextDraft);
      setExpandedAttributes({});
      setExpandedJourneySteps({});
      setExpandedJourneyStatuses({});
      setOpenJourneyContentMenuKey(null);
      setConfirmingAttributeDelete(null);
      setConfirmingJourneyDelete(null);
      setConfirmingStatusDelete(null);
      setError('Ugyldig produkt.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await DashboardApi.getCompanyProducts(selectedCompanyId, { includeArchived: true });
      const items = Array.isArray(response?.items)
        ? response.items.map(mapProductItem).filter((item) => item.id)
        : [];
      setBots(Array.isArray(response?.bots) ? response.bots.map(mapAssignedBot).filter((bot) => bot.id) : []);

      if (isNewProduct) {
        const nextDraft = attachAttributeClientKeys(createEmptyDraft());
        setProduct(null);
        setDraft(nextDraft);
        setSavedDraft(nextDraft);
        setExpandedAttributes({});
        setExpandedJourneySteps({});
        setExpandedJourneyStatuses({});
        setOpenJourneyContentMenuKey(null);
        setConfirmingAttributeDelete(null);
        setConfirmingJourneyDelete(null);
        setConfirmingStatusDelete(null);
        setError(null);
        return;
      }

      const nextProduct = items.find((item) => item.id === numericProductId) || null;
      if (!nextProduct) {
        const nextDraft = attachAttributeClientKeys(createEmptyDraft());
        setProduct(null);
        setDraft(nextDraft);
        setSavedDraft(nextDraft);
        setExpandedAttributes({});
        setExpandedJourneySteps({});
        setExpandedJourneyStatuses({});
        setOpenJourneyContentMenuKey(null);
        setConfirmingAttributeDelete(null);
        setConfirmingJourneyDelete(null);
        setConfirmingStatusDelete(null);
        setError('Fant ikke produktet.');
        return;
      }

      const nextDraft = attachAttributeClientKeys(createDraftFromProduct(nextProduct));
      setProduct(nextProduct);
      setDraft(nextDraft);
      setSavedDraft(nextDraft);
      setExpandedAttributes({});
      setExpandedJourneySteps({});
      setExpandedJourneyStatuses({});
      setOpenJourneyContentMenuKey(null);
      setConfirmingAttributeDelete(null);
      setConfirmingJourneyDelete(null);
      setConfirmingStatusDelete(null);
      setError(null);
    } catch (err) {
      const message = err.message || 'Kunne ikke hente produktet.';
      setError(message);
      notifyError(message, { source: 'Produkter' });
    } finally {
      setLoading(false);
    }
  }, [canView, hasValidProductId, isNewProduct, numericProductId, selectedCompanyId]);

  useEffect(() => {
    loadProductContext();
  }, [loadProductContext]);

  const updateDraft = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const updateInboxField = (index, patch) => {
    setDraft((prev) => {
      const fields = [...(prev.inboxConfig?.fields || [])];
      fields[index] = { ...fields[index], ...patch };
      return {
        ...prev,
        inboxConfig: { ...prev.inboxConfig, fields },
      };
    });
    setConfirmingAttributeDelete(null);
  };

  const updateJourneyStep = (index, patch) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const current = steps[index] || createCustomerJourneyStepDraft(index);
      const nextStep = {
        ...current,
        ...patch,
      };
      steps[index] = nextStep;
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
    setConfirmingJourneyDelete(null);
  };

  const addJourneyStep = () => {
    const nextStep = createCustomerJourneyStepDraft(draft.customerJourney?.steps?.length || 0);
    setDraft((prev) => ({
      ...prev,
      customerJourney: {
        ...prev.customerJourney,
        steps: [...(prev.customerJourney?.steps || []), nextStep],
      },
    }));
    setExpandedJourneySteps((prev) => ({
      ...prev,
      [getJourneyStepEntryKey(nextStep, draft.customerJourney?.steps?.length || 0)]: true,
    }));
    setConfirmingJourneyDelete(null);
  };

  const removeJourneyStep = (index) => {
    const removedKey = getJourneyStepEntryKey(draft.customerJourney?.steps?.[index], index);
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      steps.splice(index, 1);
      return {
        ...prev,
        customerJourney: {
          ...prev.customerJourney,
          steps: steps.map((step, stepIndex) => ({ ...step, sortOrder: stepIndex })),
        },
      };
    });
    setExpandedJourneySteps((prev) => {
      const next = { ...prev };
      delete next[removedKey];
      return next;
    });
    setOpenJourneyContentMenuKey((current) => (current === removedKey ? null : current));
    setConfirmingJourneyDelete(null);
  };

  const handleJourneyDeleteClick = (index, stepKey) => {
    if (!canEdit || !stepKey) return;
    if (confirmingJourneyDelete === stepKey) {
      removeJourneyStep(index);
      return;
    }
    setConfirmingJourneyDelete(stepKey);
  };

  const toggleJourneyStepExpanded = (stepKey) => {
    if (!stepKey) return;
    setExpandedJourneySteps((prev) => ({
      ...prev,
      [stepKey]: !prev[stepKey],
    }));
  };

  const moveJourneyStep = (index, direction) => {
    if (!canEdit) return;
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (index < 0 || nextIndex < 0 || index >= steps.length || nextIndex >= steps.length) {
        return prev;
      }
      const [moved] = steps.splice(index, 1);
      steps.splice(nextIndex, 0, moved);
      return {
        ...prev,
        customerJourney: {
          ...prev.customerJourney,
          steps: steps.map((step, stepIndex) => ({ ...step, sortOrder: stepIndex })),
        },
      };
    });
    setConfirmingJourneyDelete(null);
  };

  const toggleJourneyContentMenu = (stepKey) => {
    if (!stepKey) return;
    setOpenJourneyContentMenuKey((current) => (current === stepKey ? null : stepKey));
  };

  const addJourneyLogField = (stepIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex] || createCustomerJourneyStepDraft(stepIndex);
      const currentFields = sortJourneyContentArray(getJourneyStepLogFields(step));
      const nextOrder = currentFields.length > 0
        ? getJourneyContentSortOrder(currentFields[0]) + (currentFields.length / 1000)
        : getJourneyStepContentItems(step).length;
      const nextField = { ...createCustomerJourneyFieldDraft(), sortOrder: nextOrder };
      steps[stepIndex] = applyJourneyStepContentOrder({
        ...step,
        logFields: [...currentFields, nextField],
      });
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
    setOpenJourneyContentMenuKey(null);
  };

  const updateJourneyLogField = (stepIndex, fieldIndex, patch) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const fields = [...getJourneyStepLogFields(step)];
      fields[fieldIndex] = { ...fields[fieldIndex], ...patch };
      steps[stepIndex] = { ...step, logFields: fields, fields };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const removeJourneyLogField = (stepIndex, fieldIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const fields = [...getJourneyStepLogFields(step)];
      fields.splice(fieldIndex, 1);
      steps[stepIndex] = applyJourneyStepContentOrder({ ...step, logFields: fields });
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const addJourneyTask = (stepIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex] || createCustomerJourneyStepDraft(stepIndex);
      const tasks = [...(step.tasks || [])];
      const nextOrder = getJourneyStepContentItems(step).length;
      const taskGroup = {
        ...createCustomerJourneyTaskDraft(tasks.length),
        label: 'Ny oppgave',
        sortOrder: nextOrder,
        tasks: [createCustomerJourneyTaskFieldDraft(0)],
        taskStates: [],
      };
      steps[stepIndex] = applyJourneyStepContentOrder({
        ...step,
        tasks: [...tasks, taskGroup],
      });
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
    setOpenJourneyContentMenuKey(null);
  };

  const updateJourneyTask = (stepIndex, taskIndex, patch) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const tasks = [...(step.tasks || [])];
      tasks[taskIndex] = { ...tasks[taskIndex], ...patch };
      steps[stepIndex] = { ...step, tasks };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const removeJourneyTask = (stepIndex, taskIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const tasks = [...(step.tasks || [])];
      tasks.splice(taskIndex, 1);
      steps[stepIndex] = applyJourneyStepContentOrder({ ...step, tasks });
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const addJourneyTaskChild = (stepIndex, taskIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const tasks = [...(step.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const childTasks = Array.isArray(taskGroup.tasks) ? [...taskGroup.tasks] : [];
      const promotedExistingTask = childTasks.length === 0
        ? [createPromotedCustomerJourneyTaskField(taskGroup, 0, taskGroup.clientKey || taskGroup.id || taskGroup.key || taskIndex)]
        : [];
      tasks[taskIndex] = {
        ...taskGroup,
        tasks: [
          ...promotedExistingTask,
          ...childTasks,
          createCustomerJourneyTaskFieldDraft(promotedExistingTask.length + childTasks.length),
        ],
        taskStates: [],
      };
      steps[stepIndex] = { ...step, tasks };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const updateJourneyTaskChild = (stepIndex, taskIndex, childTaskIndex, patch) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const tasks = [...(step.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const childTasks = [...(taskGroup.tasks || [])];
      childTasks[childTaskIndex] = { ...childTasks[childTaskIndex], ...patch };
      tasks[taskIndex] = { ...taskGroup, tasks: childTasks };
      steps[stepIndex] = { ...step, tasks };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const removeJourneyTaskChild = (stepIndex, taskIndex, childTaskIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const tasks = [...(step.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const childTasks = [...(taskGroup.tasks || [])];
      childTasks.splice(childTaskIndex, 1);
      tasks[taskIndex] = {
        ...taskGroup,
        tasks: childTasks.map((task, index) => ({ ...task, sortOrder: index })),
      };
      steps[stepIndex] = { ...step, tasks };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const addJourneyEvent = (stepIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex] || createCustomerJourneyStepDraft(stepIndex);
      const events = [...(step.events || [])];
      const nextOrder = getJourneyStepContentItems(step).length;
      steps[stepIndex] = applyJourneyStepContentOrder({
        ...step,
        events: [...events, { ...createCustomerJourneyEventDraft(events.length), sortOrder: nextOrder }],
      });
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
    setOpenJourneyContentMenuKey(null);
  };

  const updateJourneyEvent = (stepIndex, eventIndex, patch) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      events[eventIndex] = { ...events[eventIndex], ...patch };
      steps[stepIndex] = { ...step, events };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const removeJourneyEvent = (stepIndex, eventIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      events.splice(eventIndex, 1);
      steps[stepIndex] = applyJourneyStepContentOrder({ ...step, events });
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const addJourneyEventTask = (stepIndex, eventIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      const eventItem = events[eventIndex];
      if (!eventItem) return prev;
      const tasks = Array.isArray(eventItem.tasks) ? [...eventItem.tasks] : [];
      events[eventIndex] = {
        ...eventItem,
        tasks: [
          ...tasks,
          {
            ...createCustomerJourneyTaskDraft(tasks.length),
            label: 'Ny oppgave',
            tasks: [createCustomerJourneyTaskFieldDraft(0)],
            taskStates: [],
          },
        ],
      };
      steps[stepIndex] = { ...step, events };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const updateJourneyEventTask = (stepIndex, eventIndex, taskIndex, patch) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      const eventItem = events[eventIndex];
      if (!eventItem) return prev;
      const tasks = [...(eventItem.tasks || [])];
      tasks[taskIndex] = { ...tasks[taskIndex], ...patch };
      events[eventIndex] = { ...eventItem, tasks };
      steps[stepIndex] = { ...step, events };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const removeJourneyEventTask = (stepIndex, eventIndex, taskIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      const eventItem = events[eventIndex];
      if (!eventItem) return prev;
      const tasks = [...(eventItem.tasks || [])];
      tasks.splice(taskIndex, 1);
      events[eventIndex] = {
        ...eventItem,
        tasks: tasks.map((task, index) => ({ ...task, sortOrder: index })),
      };
      steps[stepIndex] = { ...step, events };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const addJourneyEventTaskField = (stepIndex, eventIndex, taskIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      const eventItem = events[eventIndex];
      if (!eventItem) return prev;
      const tasks = [...(eventItem.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const fields = Array.isArray(taskGroup.tasks) ? [...taskGroup.tasks] : [];
      const promotedExistingTask = fields.length === 0
        ? [createPromotedCustomerJourneyTaskField(taskGroup, 0, taskGroup.clientKey || taskGroup.id || taskGroup.key || taskIndex)]
        : [];
      tasks[taskIndex] = {
        ...taskGroup,
        taskStates: [],
        tasks: [
          ...promotedExistingTask,
          ...fields,
          createCustomerJourneyTaskFieldDraft(promotedExistingTask.length + fields.length),
        ],
      };
      events[eventIndex] = { ...eventItem, tasks };
      steps[stepIndex] = { ...step, events };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const updateJourneyEventTaskField = (stepIndex, eventIndex, taskIndex, fieldIndex, patch) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      const eventItem = events[eventIndex];
      if (!eventItem) return prev;
      const tasks = [...(eventItem.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const fields = [...(taskGroup.tasks || [])];
      fields[fieldIndex] = { ...fields[fieldIndex], ...patch };
      tasks[taskIndex] = { ...taskGroup, tasks: fields };
      events[eventIndex] = { ...eventItem, tasks };
      steps[stepIndex] = { ...step, events };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const removeJourneyEventTaskField = (stepIndex, eventIndex, taskIndex, fieldIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      const eventItem = events[eventIndex];
      if (!eventItem) return prev;
      const tasks = [...(eventItem.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const fields = [...(taskGroup.tasks || [])];
      fields.splice(fieldIndex, 1);
      tasks[taskIndex] = {
        ...taskGroup,
        tasks: fields.map((field, index) => ({ ...field, sortOrder: index })),
      };
      events[eventIndex] = { ...eventItem, tasks };
      steps[stepIndex] = { ...step, events };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const addJourneyEventTaskFieldState = (stepIndex, eventIndex, taskIndex, fieldIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      const eventItem = events[eventIndex];
      if (!eventItem) return prev;
      const tasks = [...(eventItem.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const fields = [...(taskGroup.tasks || [])];
      const field = fields[fieldIndex];
      if (!field) return prev;
      const taskStates = normalizeJourneyTaskStatesForEdit(field.taskStates);
      fields[fieldIndex] = { ...field, taskStates: [...taskStates, createCustomerJourneyTaskState(taskStates.length)] };
      tasks[taskIndex] = { ...taskGroup, tasks: fields };
      events[eventIndex] = { ...eventItem, tasks };
      steps[stepIndex] = { ...step, events };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const updateJourneyEventTaskFieldState = (stepIndex, eventIndex, taskIndex, fieldIndex, stateIndex, patch) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      const eventItem = events[eventIndex];
      if (!eventItem) return prev;
      const tasks = [...(eventItem.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const fields = [...(taskGroup.tasks || [])];
      const field = fields[fieldIndex];
      if (!field) return prev;
      const taskStates = normalizeJourneyTaskStatesForEdit(field.taskStates);
      taskStates[stateIndex] = { ...taskStates[stateIndex], ...patch };
      fields[fieldIndex] = { ...field, taskStates: normalizeJourneyTaskStatesForEdit(taskStates) };
      tasks[taskIndex] = { ...taskGroup, tasks: fields };
      events[eventIndex] = { ...eventItem, tasks };
      steps[stepIndex] = { ...step, events };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const removeJourneyEventTaskFieldState = (stepIndex, eventIndex, taskIndex, fieldIndex, stateIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const events = [...(step.events || [])];
      const eventItem = events[eventIndex];
      if (!eventItem) return prev;
      const tasks = [...(eventItem.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const fields = [...(taskGroup.tasks || [])];
      const field = fields[fieldIndex];
      if (!field) return prev;
      const taskStates = normalizeJourneyTaskStatesForEdit(field.taskStates);
      taskStates.splice(stateIndex, 1);
      fields[fieldIndex] = { ...field, taskStates };
      tasks[taskIndex] = { ...taskGroup, tasks: fields };
      events[eventIndex] = { ...eventItem, tasks };
      steps[stepIndex] = { ...step, events };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const moveJourneyContentItem = (stepIndex, contentIndex, direction) => {
    if (!canEdit) return;
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const items = getJourneyStepContentItems(step);
      const nextIndex = direction === 'up' ? contentIndex - 1 : contentIndex + 1;
      if (contentIndex < 0 || nextIndex < 0 || contentIndex >= items.length || nextIndex >= items.length) {
        return prev;
      }
      const [moved] = items.splice(contentIndex, 1);
      items.splice(nextIndex, 0, moved);
      steps[stepIndex] = applyJourneyStepContentOrder(step, items);
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const addJourneyTaskChildState = (stepIndex, taskIndex, childTaskIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const tasks = [...(step.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const childTasks = [...(taskGroup.tasks || [])];
      const childTask = childTasks[childTaskIndex];
      if (!childTask) return prev;
      const taskStates = normalizeJourneyTaskStatesForEdit(childTask.taskStates);
      childTasks[childTaskIndex] = { ...childTask, taskStates: [...taskStates, createCustomerJourneyTaskState(taskStates.length)] };
      tasks[taskIndex] = { ...taskGroup, tasks: childTasks };
      steps[stepIndex] = { ...step, tasks };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const updateJourneyTaskChildState = (stepIndex, taskIndex, childTaskIndex, stateIndex, patch) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const tasks = [...(step.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const childTasks = [...(taskGroup.tasks || [])];
      const childTask = childTasks[childTaskIndex];
      if (!childTask) return prev;
      const taskStates = normalizeJourneyTaskStatesForEdit(childTask.taskStates);
      taskStates[stateIndex] = { ...taskStates[stateIndex], ...patch };
      childTasks[childTaskIndex] = { ...childTask, taskStates: normalizeJourneyTaskStatesForEdit(taskStates) };
      tasks[taskIndex] = { ...taskGroup, tasks: childTasks };
      steps[stepIndex] = { ...step, tasks };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const removeJourneyTaskChildState = (stepIndex, taskIndex, childTaskIndex, stateIndex) => {
    setDraft((prev) => {
      const steps = [...(prev.customerJourney?.steps || [])];
      const step = steps[stepIndex];
      if (!step) return prev;
      const tasks = [...(step.tasks || [])];
      const taskGroup = tasks[taskIndex];
      if (!taskGroup) return prev;
      const childTasks = [...(taskGroup.tasks || [])];
      const childTask = childTasks[childTaskIndex];
      if (!childTask) return prev;
      const taskStates = normalizeJourneyTaskStatesForEdit(childTask.taskStates);
      taskStates.splice(stateIndex, 1);
      childTasks[childTaskIndex] = { ...childTask, taskStates };
      tasks[taskIndex] = { ...taskGroup, tasks: childTasks };
      steps[stepIndex] = { ...step, tasks };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, steps },
      };
    });
  };

  const addJourneyStatus = () => {
    const nextStatus = createCustomerJourneyStatusDraft(draft.customerJourney?.statuses?.length || 0);
    setDraft((prev) => ({
      ...prev,
      customerJourney: {
        ...prev.customerJourney,
        statuses: [...(prev.customerJourney?.statuses || []), nextStatus],
      },
    }));
    setExpandedJourneyStatuses((prev) => ({
      ...prev,
      [getJourneyStatusEntryKey(nextStatus, draft.customerJourney?.statuses?.length || 0)]: true,
    }));
    setConfirmingStatusDelete(null);
  };

  const updateJourneyStatus = (index, patch) => {
    setDraft((prev) => {
      const statuses = [...(prev.customerJourney?.statuses || [])];
      statuses[index] = { ...statuses[index], ...patch };
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, statuses },
      };
    });
    setConfirmingStatusDelete(null);
  };

  const removeJourneyStatus = (index) => {
    const removedKey = getJourneyStatusEntryKey(draft.customerJourney?.statuses?.[index], index);
    setDraft((prev) => {
      const statuses = [...(prev.customerJourney?.statuses || [])];
      statuses.splice(index, 1);
      return {
        ...prev,
        customerJourney: { ...prev.customerJourney, statuses },
      };
    });
    setExpandedJourneyStatuses((prev) => {
      const next = { ...prev };
      delete next[removedKey];
      return next;
    });
    setConfirmingStatusDelete(null);
  };

  const handleJourneyStatusDeleteClick = (index, statusKey) => {
    if (!canEdit || !statusKey) return;
    if (confirmingStatusDelete === statusKey) {
      removeJourneyStatus(index);
      return;
    }
    setConfirmingStatusDelete(statusKey);
  };

  const toggleJourneyStatusExpanded = (statusKey) => {
    if (!statusKey) return;
    setExpandedJourneyStatuses((prev) => ({
      ...prev,
      [statusKey]: !prev[statusKey],
    }));
  };

  const addInboxField = () => {
    const nextField = createProductInboxFieldDraft();
    setDraft((prev) => ({
      ...prev,
      inboxConfig: {
        ...prev.inboxConfig,
        fields: [...(prev.inboxConfig?.fields || []), nextField],
      },
    }));
    setExpandedAttributes((prev) => ({
      ...prev,
      [getAttributeEntryKey(nextField, (draft.inboxConfig?.fields || []).length)]: true,
    }));
    setConfirmingAttributeDelete(null);
  };

  const removeInboxField = (index) => {
    const removedKey = getAttributeEntryKey(draft.inboxConfig?.fields?.[index], index);
    setDraft((prev) => {
      const fields = [...(prev.inboxConfig?.fields || [])];
      fields.splice(index, 1);
      return {
        ...prev,
        inboxConfig: { ...prev.inboxConfig, fields },
      };
    });
    setExpandedAttributes((prev) => {
      const next = { ...prev };
      delete next[removedKey];
      return next;
    });
    setConfirmingAttributeDelete(null);
  };

  const handleAttributeDeleteClick = (index, attributeKey) => {
    if (!canEdit || !attributeKey) return;
    if (confirmingAttributeDelete === attributeKey) {
      removeInboxField(index);
      return;
    }
    setConfirmingAttributeDelete(attributeKey);
  };

  const toggleAttributeExpanded = (attributeKey) => {
    if (!attributeKey) return;
    setExpandedAttributes((prev) => ({
      ...prev,
      [attributeKey]: !prev[attributeKey],
    }));
  };

  const moveInboxField = (index, direction) => {
    if (!canEdit) return;
    setDraft((prev) => {
      const fields = [...(prev.inboxConfig?.fields || [])];
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (index < 0 || nextIndex < 0 || index >= fields.length || nextIndex >= fields.length) {
        return prev;
      }
      const [moved] = fields.splice(index, 1);
      fields.splice(nextIndex, 0, moved);
      return {
        ...prev,
        inboxConfig: { ...prev.inboxConfig, fields },
      };
    });
    setConfirmingAttributeDelete(null);
  };

  const toggleAssignedBot = (botId) => {
    setDraft((prev) => {
      const current = new Set((prev.assignedBotIds || []).map((id) => Number(id)));
      if (current.has(botId)) current.delete(botId);
      else current.add(botId);
      return {
        ...prev,
        assignedBotIds: Array.from(current).sort((left, right) => left - right),
      };
    });
  };

  const handleTagsChange = (nextTags) => {
    updateDraft({ tagsText: nextTags.join(', ') });
  };

  const editorAvailable = isNewProduct || product;
  const customerJourneySteps = draft.customerJourney?.steps || [];
  const customerJourneyStatuses = draft.customerJourney?.statuses || [];

  const hasUnsavedChanges = useMemo(() => (
    canEdit
    && editorAvailable
    && !loading
    && JSON.stringify(draft) !== JSON.stringify(savedDraft)
  ), [canEdit, draft, editorAvailable, loading, savedDraft]);

  const discardChanges = () => {
    if (saving) return;
    setDraft(savedDraft);
    setExpandedAttributes({});
    setExpandedJourneySteps({});
    setExpandedJourneyStatuses({});
    setOpenJourneyContentMenuKey(null);
    setConfirmingAttributeDelete(null);
    setConfirmingJourneyDelete(null);
    setConfirmingStatusDelete(null);
    setError(null);
  };

  const refreshProductInBackground = (productIdToRefresh) => {
    if (!selectedCompanyId || !productIdToRefresh) return;
    DashboardApi.getCompanyProducts(selectedCompanyId, { includeArchived: true })
      .then((response) => {
        const items = Array.isArray(response?.items)
          ? response.items.map(mapProductItem).filter((item) => item.id)
          : [];
        const nextProduct = items.find((item) => item.id === Number(productIdToRefresh)) || null;
        if (nextProduct) {
          setProduct(nextProduct);
        }
      })
      .catch((err) => {
        console.warn('Kunne ikke hente produktet etter lagring.', err);
      });
  };

  const handleSave = async () => {
    if (!selectedCompanyId || !canEdit) return;
    const validationError = validateDraft(draft);
    if (validationError) {
      setError(validationError);
      notifyError(validationError, { source: 'Produkter' });
      return;
    }

    setSaving(true);
    try {
      const payload = buildProductPayload(draft);
      const inboxConfigPayload = buildInboxConfigPayload(draft);
      const customerJourneyPayload = buildCustomerJourneyPayload(draft);
      const response = isNewProduct
        ? await DashboardApi.createCompanyProduct(selectedCompanyId, payload)
        : await DashboardApi.updateCompanyProduct(selectedCompanyId, numericProductId, payload);
      const savedProductId = response?.item?.id || product?.id || numericProductId;
      if (savedProductId) {
        await DashboardApi.updateCompanyProductInboxConfig(
          selectedCompanyId,
          savedProductId,
          inboxConfigPayload,
        );
        await DashboardApi.updateCompanyProductCustomerJourney(
          selectedCompanyId,
          savedProductId,
          customerJourneyPayload,
        );
      }
      setSavedDraft(draft);
      setError(null);
      setConfirmingAttributeDelete(null);
      setConfirmingJourneyDelete(null);
      setConfirmingStatusDelete(null);
      if (response?.item) {
        setProduct((current) => ({
          ...(current || {}),
          ...mapProductItem(response.item),
        }));
      }
      notifySuccess(isNewProduct ? 'Produktet er opprettet.' : 'Produktet er lagret.', { source: 'Produkter' });
      if (isNewProduct && savedProductId) {
        navigate(`${PRODUCTS_PATH}/${savedProductId}`, { replace: true });
      } else if (savedProductId) {
        refreshProductInBackground(savedProductId);
      }
    } catch (err) {
      const message = err.message || 'Kunne ikke lagre produkt.';
      setError(message);
      notifyError(message, { source: 'Produkter' });
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedCompanyId || !canEdit || !product?.id) return;
    setSaving(true);
    try {
      if (product.status === 'archived') {
        await DashboardApi.restoreCompanyProduct(selectedCompanyId, product.id);
      } else {
        await DashboardApi.archiveCompanyProduct(selectedCompanyId, product.id);
      }
      notifySuccess(product.status === 'archived' ? 'Produktet er aktivert.' : 'Produktet er arkivert.', { source: 'Produkter' });
      await loadProductContext();
    } catch (err) {
      notifyError(err.message || 'Kunne ikke endre produktstatus.', { source: 'Produkter' });
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page product-editor-page">
      <div className="product-editor-back-row">
        <button type="button" className="button button-secondary button-small" onClick={() => navigate(PRODUCTS_PATH)}>
          Tilbake til produkter
        </button>
      </div>

      <div className="page-header product-editor-header">
        <div className="product-editor-title">
          <ProductSwatch color={draft.colorCode} className="product-editor-title-swatch" />
          <div>
            <h1>{isNewProduct ? 'Nytt produkt' : 'Rediger produkt'}</h1>
            <p className="page-subtitle">
              {draft.name?.trim() || 'Produkt uten navn'} · detaljer, bot-tilgang og intern oppfølging
            </p>
          </div>
        </div>
        <div className="product-editor-actions">
          {product && (
            <span className={`products-status products-status--${product.status}`}>
              {PRODUCT_STATUS_LABELS[product.status] || product.status}
            </span>
          )}
          {product && (
            <button type="button" className="button button-secondary" onClick={handleArchive} disabled={!canEdit || saving}>
              {product.status === 'archived' ? 'Aktiver produkt' : 'Arkiver produkt'}
            </button>
          )}
          <button type="button" className="button button-secondary" onClick={() => navigate(PRODUCTS_PATH)} disabled={saving}>
            Avbryt
          </button>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}

      {loading ? (
        <div className="products-empty">Laster produkt…</div>
      ) : !editorAvailable ? (
        <section className="products-section">
          <div className="products-empty">Produktet finnes ikke eller er ikke tilgjengelig for dette selskapet.</div>
        </section>
      ) : (
        <div className="product-editor-layout">
          <main className="product-editor-main">
            <section className="product-editor-section">
              <h2>Produktdetaljer</h2>
              <label className="form-field">
                <span>Navn</span>
                <input className="input" type="text" value={draft.name} onChange={(event) => updateDraft({ name: event.target.value })} disabled={!canEdit || saving} />
              </label>
              <label className="form-field">
                <span>Beskrivelse</span>
                <textarea className="textarea" rows={5} value={draft.shortDescription} onChange={(event) => updateDraft({ shortDescription: event.target.value })} disabled={!canEdit || saving} />
              </label>
              <div className="products-form-grid">
                <label className="form-field">
                  <span>Kategori</span>
                  <input className="input" type="text" value={draft.category} onChange={(event) => updateDraft({ category: event.target.value })} disabled={!canEdit || saving} />
                </label>
                <label className="form-field">
                  <span>Pris</span>
                  <input className="input" type="number" min="0" step="0.01" value={draft.priceAmount} onChange={(event) => updateDraft({ priceAmount: event.target.value })} disabled={!canEdit || saving} />
                </label>
                <label className="form-field">
                  <span>Valuta</span>
                  <input className="input" type="text" value={draft.priceCurrency} onChange={(event) => updateDraft({ priceCurrency: event.target.value.toUpperCase() })} disabled={!canEdit || saving} />
                </label>
                <label className="form-field products-form-grid-wide">
                  <span>Prisvisning</span>
                  <input className="input" type="text" value={draft.priceDisplay} onChange={(event) => updateDraft({ priceDisplay: event.target.value })} disabled={!canEdit || saving} />
                </label>
                <label className="form-field products-form-grid-wide">
                  <span>URL</span>
                  <input className="input" type="url" value={draft.url} onChange={(event) => updateDraft({ url: event.target.value })} disabled={!canEdit || saving} />
                </label>
                <div className="label products-form-grid-wide">
                  <span className="label-text">Tags</span>
                  <SelectOptionsEditor
                    options={String(draft.tagsText || '').split(/[,;|]/u).map((tag) => tag.trim()).filter(Boolean)}
                    onChange={handleTagsChange}
                    disabled={!canEdit || saving}
                    addButtonLabel="Legg til tag"
                    inputLabel="Ny tag"
                    editLabel="Rediger tag"
                  />
                </div>
                <label className="form-field products-color-field">
                  <span>Fargekode</span>
                  <span className="products-color-control">
                    <input type="color" value={normalizeColor(draft.colorCode)} onChange={(event) => updateDraft({ colorCode: event.target.value })} disabled={!canEdit || saving} />
                    <input className="input" type="text" value={draft.colorCode} onChange={(event) => updateDraft({ colorCode: event.target.value })} disabled={!canEdit || saving} />
                  </span>
                </label>
              </div>
            </section>

            <section className="product-editor-section">
              <div className="products-panel-title-row">
                <h2>Produktspesifikke inbox-felt</h2>
                <button type="button" className="button button-secondary button-small" onClick={addInboxField} disabled={!canEdit || saving}>
                  Legg til felt
                </button>
              </div>
              {(draft.inboxConfig?.fields || []).length === 0 ? (
                <div className="products-empty products-empty--compact">Ingen produktspesifikke felt.</div>
              ) : (
                <div className="product-inbox-config-list">
                  {(draft.inboxConfig?.fields || []).map((field, index) => {
                    const attributeKey = getAttributeEntryKey(field, index);
                    const isAttributeExpanded = Boolean(expandedAttributes[attributeKey]);
                    const confirmingDelete = confirmingAttributeDelete === attributeKey;
                    const attributeLabel = field.label || field.key || `Attributt ${index + 1}`;
                    return (
                      <article key={attributeKey} className={`product-inbox-config-row inquiry-attribute-row product-attribute-row${isAttributeExpanded ? ' is-expanded' : ' is-collapsed'}`}>
                        <header className="inquiry-attribute-summary">
                          <div className="inquiry-attribute-summary-main">
                            <div className="inquiry-attribute-title-line">
                              <strong>{attributeLabel}</strong>
                            </div>
                            <span>{FIELD_TYPE_OPTIONS.find((option) => option.value === field.type)?.label || field.type}</span>
                          </div>
                          <div className="inquiry-attribute-summary-actions">
                            <button
                              type="button"
                              className="button button-secondary button-small inquiry-attribute-icon-button"
                              onClick={() => moveInboxField(index, 'up')}
                              disabled={!canEdit || saving || index === 0}
                              aria-label={`Flytt ${attributeLabel} opp`}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="button button-secondary button-small inquiry-attribute-icon-button"
                              onClick={() => moveInboxField(index, 'down')}
                              disabled={!canEdit || saving || index === (draft.inboxConfig?.fields || []).length - 1}
                              aria-label={`Flytt ${attributeLabel} ned`}
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              className="button button-secondary button-small"
                              onClick={() => toggleAttributeExpanded(attributeKey)}
                              disabled={saving}
                              aria-expanded={isAttributeExpanded}
                            >
                              {isAttributeExpanded ? 'Lukk' : 'Utvid'}
                            </button>
                            <button
                              type="button"
                              className={`button button-small ${confirmingDelete ? 'button-danger' : 'button-secondary'} inquiry-attribute-delete-button`}
                              onClick={() => handleAttributeDeleteClick(index, attributeKey)}
                              disabled={!canEdit || saving}
                              aria-label={confirmingDelete ? `Bekreft sletting av ${attributeLabel}` : `Slett ${attributeLabel}`}
                            >
                              {confirmingDelete ? 'Bekreft' : <TbTrash aria-hidden="true" focusable="false" />}
                            </button>
                          </div>
                        </header>

                        {isAttributeExpanded ? (
                          <>
                            <div className="products-form-grid inquiry-attribute-fields">
                              <label className="form-field">
                                <span>Nøkkel</span>
                                <input className="input" type="text" value={field.key} onChange={(event) => updateInboxField(index, { key: event.target.value })} disabled={!canEdit || saving} />
                              </label>
                              <label className="form-field">
                                <span>Etikett</span>
                                <input className="input" type="text" value={field.label} onChange={(event) => updateInboxField(index, { label: event.target.value })} disabled={!canEdit || saving} />
                              </label>
                              <label className="form-field">
                                <span>Type</span>
                                <select className="select" value={field.type} onChange={(event) => updateInboxField(index, { type: event.target.value })} disabled={!canEdit || saving}>
                                  {FIELD_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="form-field products-form-grid-wide">
                                <span>Hjelpetekst</span>
                                <input className="input" type="text" value={field.helpText} onChange={(event) => updateInboxField(index, { helpText: event.target.value })} disabled={!canEdit || saving} />
                              </label>
                              {field.type === 'select' ? (
                                <div className="label products-form-grid-wide">
                                  <span className="label-text">Valg</span>
                                  <SelectOptionsEditor
                                    options={field.options || []}
                                    onChange={(nextOptions) => updateInboxField(index, { options: nextOptions })}
                                    disabled={!canEdit || saving}
                                  />
                                </div>
                              ) : null}
                            </div>
                            <div className="products-field-row-options">
                              <label><input type="checkbox" checked={field.collectInChat === true} onChange={(event) => updateInboxField(index, { collectInChat: event.target.checked })} disabled={!canEdit || saving} /> Samle i chat</label>
                              <label><input type="checkbox" checked={field.required === true} onChange={(event) => updateInboxField(index, { required: event.target.checked })} disabled={!canEdit || saving || field.collectInChat !== true} /> Påkrevd</label>
                              <label><input type="checkbox" checked={field.showInInboxList === true} onChange={(event) => updateInboxField(index, { showInInboxList: event.target.checked })} disabled={!canEdit || saving} /> Vis i liste</label>
                            </div>
                          </>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="product-editor-section product-editor-journey-section">
              <div className="products-panel-title-row">
                <h2>Kundeløp</h2>
                <button type="button" className="button button-secondary button-small" onClick={addJourneyStep} disabled={!canEdit || saving}>
                  Legg til steg
                </button>
              </div>
              {customerJourneySteps.length === 0 ? (
                <div className="products-empty products-empty--compact">Ingen kundeløp-steg.</div>
              ) : (
                <div className="product-inbox-config-list customer-journey-config-list">
                  {customerJourneySteps.map((step, index) => {
                    const stepKey = getJourneyStepEntryKey(step, index);
                    const isStepExpanded = Boolean(expandedJourneySteps[stepKey]);
                    const confirmingDelete = confirmingJourneyDelete === stepKey;
                    const stepLabel = step.label || `Steg ${index + 1}`;
                    const contentItems = getJourneyStepContentItems(step);
                    const isContentMenuOpen = openJourneyContentMenuKey === stepKey;
                    return (
                      <article key={stepKey} className={`product-inbox-config-row inquiry-attribute-row product-attribute-row journey-depth-0${isStepExpanded ? ' is-expanded' : ' is-collapsed'}`}>
                        <header className="inquiry-attribute-summary">
                          <div className="inquiry-attribute-summary-main">
                            <div className="inquiry-attribute-title-line">
                              <strong>{stepLabel}</strong>
                              <RequiredPillToggle
                                checked={step.required !== false}
                                onChange={(nextRequired) => updateJourneyStep(index, { required: nextRequired })}
                                disabled={!canEdit || saving}
                              />
                            </div>
                            <span>
                              {getJourneyStepTaskCount(step)} oppgaver · {(step.events || []).length} avtaler · {(step.logFields || step.fields || []).length} loggfelt
                            </span>
                          </div>
                          <div className="inquiry-attribute-summary-actions">
                            <button
                              type="button"
                              className="button button-secondary button-small inquiry-attribute-icon-button"
                              onClick={() => moveJourneyStep(index, 'up')}
                              disabled={!canEdit || saving || index === 0}
                              aria-label={`Flytt ${stepLabel} opp`}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="button button-secondary button-small inquiry-attribute-icon-button"
                              onClick={() => moveJourneyStep(index, 'down')}
                              disabled={!canEdit || saving || index === (draft.customerJourney?.steps || []).length - 1}
                              aria-label={`Flytt ${stepLabel} ned`}
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              className="button button-secondary button-small"
                              onClick={() => toggleJourneyStepExpanded(stepKey)}
                              disabled={saving}
                              aria-expanded={isStepExpanded}
                            >
                              {isStepExpanded ? 'Lukk' : 'Utvid'}
                            </button>
                            <button
                              type="button"
                              className={`button button-small ${confirmingDelete ? 'button-danger' : 'button-secondary'} inquiry-attribute-delete-button`}
                              onClick={() => handleJourneyDeleteClick(index, stepKey)}
                              disabled={!canEdit || saving}
                              aria-label={confirmingDelete ? `Bekreft sletting av ${stepLabel}` : `Slett ${stepLabel}`}
                            >
                              {confirmingDelete ? 'Bekreft' : <TbTrash aria-hidden="true" focusable="false" />}
                            </button>
                          </div>
                        </header>

                        {isStepExpanded ? (
                          <>
                            <div className="customer-journey-object-title-row customer-journey-object-title-row--step customer-journey-object-title-row--no-toggle">
                              <label className="form-field">
                                <span>Etikett</span>
                                <input className="input" type="text" value={step.label} onChange={(event) => updateJourneyStep(index, { label: event.target.value })} disabled={!canEdit || saving} />
                              </label>
                              <label className="form-field">
                                <span>Henvendelsesprioritet</span>
                                <span className="customer-journey-percent-input">
                                  <input
                                    className="input"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={normalizeCustomerJourneyPriority(step.inquiryPriority ?? step.inquiry_priority)}
                                    onChange={(event) => updateJourneyStep(index, { inquiryPriority: event.target.value })}
                                    disabled={!canEdit || saving}
                                  />
                                  <span aria-hidden="true">/100</span>
                                </span>
                              </label>
                            </div>

                            <div className="customer-journey-content-list">
                              <div className="products-panel-title-row products-panel-title-row--compact">
                                <h3>Innhold</h3>
                                <div className="customer-journey-add-menu-wrap">
                                  <button
                                    type="button"
                                    className="button button-secondary button-small"
                                    onClick={() => toggleJourneyContentMenu(stepKey)}
                                    disabled={!canEdit || saving}
                                    aria-expanded={isContentMenuOpen}
                                  >
                                    Legg til
                                  </button>
                                  {isContentMenuOpen ? (
                                    <div className="customer-journey-add-menu" role="menu">
                                      <button type="button" role="menuitem" onClick={() => addJourneyTask(index)} disabled={!canEdit || saving}>
                                        Oppgave
                                      </button>
                                      <button type="button" role="menuitem" onClick={() => addJourneyEvent(index)} disabled={!canEdit || saving}>
                                        Avtale
                                      </button>
                                      <button type="button" role="menuitem" onClick={() => addJourneyLogField(index)} disabled={!canEdit || saving}>
                                        Loggfelt
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                              {contentItems.length === 0 ? (
                                <div className="products-empty products-empty--compact">Ingen innholdselementer.</div>
                              ) : (
                                <div className="customer-journey-content-items">
                                  {contentItems.map((entry, contentIndex) => {
                                    const { kind, item, itemIndex } = entry;
                                    const itemLabel = item.label || (
                                      kind === 'task'
                                        ? `Oppgave ${itemIndex + 1}`
                                        : kind === 'event'
                                          ? `Avtale ${itemIndex + 1}`
                                          : 'Loggfelt'
                                    );
                                    const itemKey = kind === 'task'
                                      ? getJourneyTaskEntryKey(item, itemIndex)
                                      : kind === 'event'
                                        ? getJourneyEventEntryKey(item, itemIndex)
                                        : `log-${stepKey}`;
                                    return (
                                      <div className="customer-journey-content-item journey-depth-1" key={`${kind}-${itemKey}`}>
                                        <div className="customer-journey-content-item-header">
                                          <div className="customer-journey-task-state-meta">
                                            <strong>{itemLabel}</strong>
                                            <small>
                                              {kind === 'task'
                                                ? `Oppgave · ${Array.isArray(item.tasks) && item.tasks.length > 0 ? `${item.tasks.length} felter` : `${normalizeJourneyTaskStatesForEdit(item.taskStates).length} statuser`}`
                                                : kind === 'event'
                                                  ? 'Avtale'
                                                  : `${item.fields?.length || 0} kolonner`}
                                            </small>
                                          </div>
                                          <div className="inquiry-attribute-summary-actions">
                                            {kind !== 'log' ? (
                                              <RequiredPillToggle
                                                checked={item.required !== false}
                                                onChange={(nextRequired) => {
                                                  if (kind === 'task') updateJourneyTask(index, itemIndex, { required: nextRequired });
                                                  else if (kind === 'event') updateJourneyEvent(index, itemIndex, { required: nextRequired });
                                                }}
                                                disabled={!canEdit || saving}
                                              />
                                            ) : null}
                                            <button
                                              type="button"
                                              className="button button-secondary button-small inquiry-attribute-icon-button"
                                              onClick={() => moveJourneyContentItem(index, contentIndex, 'up')}
                                              disabled={!canEdit || saving || contentIndex === 0}
                                              aria-label={`Flytt ${itemLabel} opp`}
                                            >
                                              ↑
                                            </button>
                                            <button
                                              type="button"
                                              className="button button-secondary button-small inquiry-attribute-icon-button"
                                              onClick={() => moveJourneyContentItem(index, contentIndex, 'down')}
                                              disabled={!canEdit || saving || contentIndex === contentItems.length - 1}
                                              aria-label={`Flytt ${itemLabel} ned`}
                                            >
                                              ↓
                                            </button>
                                          </div>
                                        </div>

                                        {kind === 'task' ? (
                                          <>
                                            <div className="customer-journey-object-title-row">
                                              <label className="form-field">
                                                <span>Oppgave</span>
                                                <input
                                                  className="input"
                                                  type="text"
                                                  value={item.label}
                                                  onChange={(event) => updateJourneyTask(index, itemIndex, { label: event.target.value })}
                                                  disabled={!canEdit || saving}
                                                />
                                              </label>
                                            </div>
                                            <div className="products-field-row-options">
                                              <button type="button" className="button button-secondary button-small" onClick={() => addJourneyTaskChild(index, itemIndex)} disabled={!canEdit || saving}>
                                                + Legg til felt
                                              </button>
                                              <button type="button" className="button button-secondary button-small" onClick={() => removeJourneyTask(index, itemIndex)} disabled={!canEdit || saving}>
                                                Fjern oppgave
                                              </button>
                                            </div>
                                            <div className="customer-journey-event-task-list">
                                              <div className="customer-journey-child-list-title">Felter</div>
                                              {item.tasks.map((childTask, childTaskIndex) => {
                                                const childTaskStates = normalizeJourneyTaskStatesForEdit(childTask.taskStates);
                                                const childTaskLabel = childTask.label || `Felt ${childTaskIndex + 1}`;
                                                return (
                                                  <div className="customer-journey-event-task journey-depth-2" key={getJourneyEventTaskEntryKey(childTask, childTaskIndex)}>
                                                    <div className="customer-journey-event-task-header">
                                                      <span className="customer-journey-event-task-label">{childTaskLabel}</span>
                                                      <RequiredPillToggle
                                                        checked={childTask.required !== false}
                                                        onChange={(nextRequired) => updateJourneyTaskChild(index, itemIndex, childTaskIndex, { required: nextRequired })}
                                                        disabled={!canEdit || saving}
                                                      />
                                                      <button type="button" className="button button-secondary button-small customer-journey-event-task-delete" onClick={() => removeJourneyTaskChild(index, itemIndex, childTaskIndex)} disabled={!canEdit || saving}>
                                                        Fjern
                                                      </button>
                                                    </div>
                                                    <div className="customer-journey-event-task-body">
                                                      <div className="customer-journey-event-task-row">
                                                        <label className="form-field">
                                                          <span>Felt</span>
                                                          <input
                                                            className="input"
                                                            type="text"
                                                            value={childTask.label}
                                                            onChange={(event) => updateJourneyTaskChild(index, itemIndex, childTaskIndex, { label: event.target.value })}
                                                            disabled={!canEdit || saving}
                                                          />
                                                        </label>
                                                        <label className="form-field">
                                                          <span>Type</span>
                                                          <select
                                                            className="select"
                                                            value={childTask.inputType || 'toggle'}
                                                            onChange={(event) => updateJourneyTaskChild(index, itemIndex, childTaskIndex, { inputType: event.target.value })}
                                                            disabled={!canEdit || saving}
                                                          >
                                                            {CUSTOMER_JOURNEY_TASK_INPUT_TYPE_OPTIONS.map((option) => (
                                                              <option key={option.value} value={option.value}>{option.label}</option>
                                                            ))}
                                                          </select>
                                                        </label>
                                                        {(childTask.inputType || 'toggle') === 'toggle' ? (
                                                          <button type="button" className="button button-secondary button-small" onClick={() => addJourneyTaskChildState(index, itemIndex, childTaskIndex)} disabled={!canEdit || saving}>
                                                            Legg til status
                                                          </button>
                                                        ) : null}
                                                      </div>
                                                    {(childTask.inputType || 'toggle') !== 'toggle' ? null : childTaskStates.length === 0 ? (
                                                      <div className="products-empty products-empty--compact">Ingen statuser for {childTaskLabel}.</div>
                                                    ) : (
                                                      <div className="customer-journey-event-task-status-list">
                                                        {childTaskStates.map((state, stateIndex) => (
                                                          <div className="customer-journey-event-task-status" key={state.key}>
                                                            <label className="form-field">
                                                              <span>Visningsnavn</span>
                                                              <input
                                                                className="input"
                                                                type="text"
                                                                value={state.label}
                                                                onChange={(event) => updateJourneyTaskChildState(index, itemIndex, childTaskIndex, stateIndex, { label: event.target.value })}
                                                                disabled={!canEdit || saving}
                                                              />
                                                            </label>
                                                            <label className="form-field">
                                                              <span>Fullføring</span>
                                                              <span className="customer-journey-percent-input">
                                                                <input
                                                                  className="input"
                                                                  type="number"
                                                                  min="0"
                                                                  max="100"
                                                                  step="5"
                                                                  value={state.completion}
                                                                  onChange={(event) => updateJourneyTaskChildState(index, itemIndex, childTaskIndex, stateIndex, { completion: event.target.value })}
                                                                  disabled={!canEdit || saving}
                                                                />
                                                                <span aria-hidden="true">%</span>
                                                              </span>
                                                            </label>
                                                            <label className="form-field">
                                                              <span>Farge</span>
                                                              <select
                                                                className="select"
                                                                value={state.color}
                                                                onChange={(event) => updateJourneyTaskChildState(index, itemIndex, childTaskIndex, stateIndex, { color: event.target.value })}
                                                                disabled={!canEdit || saving}
                                                              >
                                                                {CUSTOMER_JOURNEY_TASK_STATE_COLOR_OPTIONS.map((option) => (
                                                                  <option key={option.value} value={option.value}>{option.label}</option>
                                                                ))}
                                                              </select>
                                                            </label>
                                                            <button
                                                              type="button"
                                                              className="button button-secondary button-small"
                                                              onClick={() => removeJourneyTaskChildState(index, itemIndex, childTaskIndex, stateIndex)}
                                                              disabled={!canEdit || saving}
                                                            >
                                                              Fjern
                                                            </button>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </>
                                        ) : null}

                                        {kind === 'event' ? (
                                          <>
                                            <div className="customer-journey-object-title-row">
                                              <label className="form-field">
                                                <span>Avtale</span>
                                                <input
                                                  className="input"
                                                  type="text"
                                                  value={item.label}
                                                  onChange={(event) => updateJourneyEvent(index, itemIndex, { label: event.target.value })}
                                                  disabled={!canEdit || saving}
                                                />
                                              </label>
                                              <label className="form-field">
                                                <span>Møte tittel</span>
                                                <input
                                                  className="input"
                                                  type="text"
                                                  value={item.meetingTitle || ''}
                                                  onChange={(event) => updateJourneyEvent(index, itemIndex, { meetingTitle: event.target.value })}
                                                  disabled={!canEdit || saving}
                                                  placeholder="F.eks. &quot;Oppstartsmøte - {navn}&quot;"
                                                />
                                              </label>
                                            </div>
                                            <div className="products-field-row-options">
                                              <button type="button" className="button button-secondary button-small" onClick={() => addJourneyEventTask(index, itemIndex)} disabled={!canEdit || saving}>
                                                + Legg til oppgave
                                              </button>
                                              <button type="button" className="button button-secondary button-small" onClick={() => removeJourneyEvent(index, itemIndex)} disabled={!canEdit || saving}>
                                                Fjern avtale
                                              </button>
                                            </div>
                                            {(item.tasks || []).length > 0 ? (
                                              <div className="customer-journey-event-task-list">
                                                <div className="customer-journey-child-list-title">Oppgaver i avtalen</div>
                                                {(item.tasks || []).map((eventTask, eventTaskIndex) => {
                                                  return (
                                                    <div className="customer-journey-event-task journey-depth-2" key={getJourneyEventTaskEntryKey(eventTask, eventTaskIndex)}>
                                                      <div className="customer-journey-event-task-header">
                                                        <span className="customer-journey-event-task-label">{eventTask.label || `Oppgave ${eventTaskIndex + 1}`}</span>
                                                        <RequiredPillToggle
                                                          checked={eventTask.required !== false}
                                                          onChange={(nextRequired) => updateJourneyEventTask(index, itemIndex, eventTaskIndex, { required: nextRequired })}
                                                          disabled={!canEdit || saving}
                                                        />
                                                        <button type="button" className="button button-secondary button-small customer-journey-event-task-delete" onClick={() => removeJourneyEventTask(index, itemIndex, eventTaskIndex)} disabled={!canEdit || saving}>
                                                          Fjern oppgave
                                                        </button>
                                                      </div>
                                                      <div className="customer-journey-event-task-body">
                                                        <div className="customer-journey-event-task-row">
                                                          <label className="form-field">
                                                            <span>Oppgave</span>
                                                            <input
                                                              className="input"
                                                              type="text"
                                                              value={eventTask.label}
                                                              onChange={(event) => updateJourneyEventTask(index, itemIndex, eventTaskIndex, { label: event.target.value })}
                                                              disabled={!canEdit || saving}
                                                            />
                                                          </label>
                                                          <button type="button" className="button button-secondary button-small" onClick={() => addJourneyEventTaskField(index, itemIndex, eventTaskIndex)} disabled={!canEdit || saving}>
                                                            + Legg til felt
                                                          </button>
                                                        </div>
                                                      <div className="customer-journey-event-task-list customer-journey-event-task-field-list">
                                                        <div className="customer-journey-child-list-title">Felter</div>
                                                        {(eventTask.tasks || []).map((field, fieldIndex) => {
                                                          const fieldStates = normalizeJourneyTaskStatesForEdit(field.taskStates);
                                                          const fieldLabel = field.label || `Felt ${fieldIndex + 1}`;
                                                          return (
                                                            <div className="customer-journey-event-task journey-depth-3" key={getJourneyEventTaskEntryKey(field, fieldIndex)}>
                                                              <div className="customer-journey-event-task-header">
                                                                <span className="customer-journey-event-task-label">{fieldLabel}</span>
                                                                <RequiredPillToggle
                                                                  checked={field.required !== false}
                                                                  onChange={(nextRequired) => updateJourneyEventTaskField(index, itemIndex, eventTaskIndex, fieldIndex, { required: nextRequired })}
                                                                  disabled={!canEdit || saving}
                                                                />
                                                                <button type="button" className="button button-secondary button-small customer-journey-event-task-delete" onClick={() => removeJourneyEventTaskField(index, itemIndex, eventTaskIndex, fieldIndex)} disabled={!canEdit || saving}>
                                                                  Fjern
                                                                </button>
                                                              </div>
                                                              <div className="customer-journey-event-task-body">
                                                                <div className="customer-journey-event-task-row">
                                                                  <label className="form-field">
                                                                    <span>Felt</span>
                                                                    <input
                                                                      className="input"
                                                                      type="text"
                                                                      value={field.label}
                                                                      onChange={(event) => updateJourneyEventTaskField(index, itemIndex, eventTaskIndex, fieldIndex, { label: event.target.value })}
                                                                      disabled={!canEdit || saving}
                                                                    />
                                                                  </label>
                                                                  <label className="form-field">
                                                                    <span>Type</span>
                                                                    <select
                                                                      className="select"
                                                                      value={field.inputType || 'toggle'}
                                                                      onChange={(event) => updateJourneyEventTaskField(index, itemIndex, eventTaskIndex, fieldIndex, { inputType: event.target.value })}
                                                                      disabled={!canEdit || saving}
                                                                    >
                                                                      {CUSTOMER_JOURNEY_TASK_INPUT_TYPE_OPTIONS.map((option) => (
                                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                                      ))}
                                                                    </select>
                                                                  </label>
                                                                  {(field.inputType || 'toggle') === 'toggle' ? (
                                                                    <button type="button" className="button button-secondary button-small" onClick={() => addJourneyEventTaskFieldState(index, itemIndex, eventTaskIndex, fieldIndex)} disabled={!canEdit || saving}>
                                                                      Legg til status
                                                                    </button>
                                                                  ) : null}
                                                                </div>
                                                              {(field.inputType || 'toggle') !== 'toggle' ? null : fieldStates.length === 0 ? (
                                                                <div className="products-empty products-empty--compact">Ingen statuser for {fieldLabel}.</div>
                                                              ) : (
                                                                <div className="customer-journey-event-task-status-list">
                                                                  {fieldStates.map((state, stateIndex) => (
                                                                    <div className="customer-journey-event-task-status" key={state.key}>
                                                                      <label className="form-field">
                                                                        <span>Visningsnavn</span>
                                                                        <input
                                                                          className="input"
                                                                          type="text"
                                                                          value={state.label}
                                                                          onChange={(event) => updateJourneyEventTaskFieldState(index, itemIndex, eventTaskIndex, fieldIndex, stateIndex, { label: event.target.value })}
                                                                          disabled={!canEdit || saving}
                                                                        />
                                                                      </label>
                                                                      <label className="form-field">
                                                                        <span>Fullføring</span>
                                                                        <span className="customer-journey-percent-input">
                                                                          <input
                                                                            className="input"
                                                                            type="number"
                                                                            min="0"
                                                                            max="100"
                                                                            step="5"
                                                                            value={state.completion}
                                                                            onChange={(event) => updateJourneyEventTaskFieldState(index, itemIndex, eventTaskIndex, fieldIndex, stateIndex, { completion: event.target.value })}
                                                                            disabled={!canEdit || saving}
                                                                          />
                                                                          <span aria-hidden="true">%</span>
                                                                        </span>
                                                                      </label>
                                                                      <label className="form-field">
                                                                        <span>Farge</span>
                                                                        <select
                                                                          className="select"
                                                                          value={state.color}
                                                                          onChange={(event) => updateJourneyEventTaskFieldState(index, itemIndex, eventTaskIndex, fieldIndex, stateIndex, { color: event.target.value })}
                                                                          disabled={!canEdit || saving}
                                                                        >
                                                                          {CUSTOMER_JOURNEY_TASK_STATE_COLOR_OPTIONS.map((option) => (
                                                                            <option key={option.value} value={option.value}>{option.label}</option>
                                                                          ))}
                                                                        </select>
                                                                      </label>
                                                                      <button
                                                                        type="button"
                                                                        className="button button-secondary button-small"
                                                                        onClick={() => removeJourneyEventTaskFieldState(index, itemIndex, eventTaskIndex, fieldIndex, stateIndex)}
                                                                        disabled={!canEdit || saving}
                                                                      >
                                                                        Fjern
                                                                      </button>
                                                                    </div>
                                                                  ))}
                                                                </div>
                                                              )}
                                                            </div>
                                                            </div>
                                                          );
                                                        })}
                                                      </div>
                                                    </div>
                                                  </div>
                                                  );
                                                })}
                                              </div>
                                            ) : null}
                                          </>
                                        ) : null}

                                        {kind === 'log' ? (
                                          <>
                                            <div className="products-field-row-options">
                                              <button type="button" className="button button-secondary button-small" onClick={() => addJourneyLogField(index)} disabled={!canEdit || saving}>
                                                Legg til kolonne
                                              </button>
                                            </div>
                                            <div className="customer-journey-event-task-list">
                                              {(item.fields || []).map((field, fieldIndex) => (
                                                <div className="customer-journey-event-task journey-depth-2" key={getJourneyFieldEntryKey(field, fieldIndex)}>
                                                  <div className="customer-journey-event-task-header">
                                                    <span className="customer-journey-event-task-label">{field.label || `Kolonne ${fieldIndex + 1}`}</span>
                                                    <RequiredPillToggle
                                                      checked={field.required !== false}
                                                      onChange={(nextRequired) => updateJourneyLogField(index, fieldIndex, { required: nextRequired })}
                                                      disabled={!canEdit || saving}
                                                    />
                                                    <button type="button" className="button button-secondary button-small customer-journey-event-task-delete" onClick={() => removeJourneyLogField(index, fieldIndex)} disabled={!canEdit || saving}>
                                                      Fjern kolonne
                                                    </button>
                                                  </div>
                                                  <div className="customer-journey-event-task-body">
                                                    <div className="customer-journey-event-task-row">
                                                      <label className="form-field">
                                                        <span>Etikett</span>
                                                        <input className="input" type="text" value={field.label} onChange={(event) => updateJourneyLogField(index, fieldIndex, { label: event.target.value })} disabled={!canEdit || saving} />
                                                      </label>
                                                      <label className="form-field">
                                                        <span>Type</span>
                                                        <select className="select" value={field.type} onChange={(event) => updateJourneyLogField(index, fieldIndex, { type: event.target.value })} disabled={!canEdit || saving}>
                                                          {FIELD_TYPE_OPTIONS.map((option) => (
                                                            <option key={option.value} value={option.value}>{option.label}</option>
                                                          ))}
                                                        </select>
                                                      </label>
                                                    </div>
                                                  <div className="products-form-grid inquiry-attribute-fields">
                                                    <label className="form-field products-form-grid-wide">
                                                      <span>Hjelpetekst</span>
                                                      <input className="input" type="text" value={field.helpText} onChange={(event) => updateJourneyLogField(index, fieldIndex, { helpText: event.target.value })} disabled={!canEdit || saving} />
                                                    </label>
                                                    {field.type === 'select' ? (
                                                      <div className="label products-form-grid-wide">
                                                        <span className="label-text">Valg</span>
                                                        <SelectOptionsEditor
                                                          options={field.options || []}
                                                          onChange={(nextOptions) => updateJourneyLogField(index, fieldIndex, { options: nextOptions })}
                                                          disabled={!canEdit || saving}
                                                        />
                                                      </div>
                                                    ) : null}
                                                  </div>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </>
                                        ) : null}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </>
                        ) : null}
                        </article>
                      );
                    })}
                  </div>
              )}

              <div className="customer-journey-terminal-statuses">
                <div className="products-panel-title-row products-panel-title-row--compact">
                  <h3>Sluttstatuser</h3>
                  <button type="button" className="button button-secondary button-small" onClick={addJourneyStatus} disabled={!canEdit || saving}>
                    Legg til status
                  </button>
                </div>
                {customerJourneyStatuses.length === 0 ? (
                  <div className="products-empty products-empty--compact">Ingen sluttstatuser. Legg til statuser som skal markere henvendelsen som fullført.</div>
                ) : (
                  <div className="product-inbox-config-list customer-journey-status-list">
                    {customerJourneyStatuses.map((status, index) => {
                      const statusKey = getJourneyStatusEntryKey(status, index);
                      const isStatusExpanded = Boolean(expandedJourneyStatuses[statusKey]);
                      const confirmingDelete = confirmingStatusDelete === statusKey;
                      const statusLabel = status.label || `Status ${index + 1}`;
                      return (
                        <article key={statusKey} className={`product-inbox-config-row inquiry-attribute-row product-attribute-row${isStatusExpanded ? ' is-expanded' : ' is-collapsed'}`}>
                          <header className="inquiry-attribute-summary">
                            <div className="inquiry-attribute-summary-main">
                              <div className="inquiry-attribute-title-line">
                                <strong>{statusLabel}</strong>
                              </div>
                              <span>Sluttstatus · 100% fullført</span>
                            </div>
                            <div className="inquiry-attribute-summary-actions">
                              <button
                                type="button"
                                className="button button-secondary button-small"
                                onClick={() => toggleJourneyStatusExpanded(statusKey)}
                                disabled={saving}
                                aria-expanded={isStatusExpanded}
                              >
                                {isStatusExpanded ? 'Lukk' : 'Utvid'}
                              </button>
                              <button
                                type="button"
                                className={`button button-small ${confirmingDelete ? 'button-danger' : 'button-secondary'} inquiry-attribute-delete-button`}
                                onClick={() => handleJourneyStatusDeleteClick(index, statusKey)}
                                disabled={!canEdit || saving}
                                aria-label={confirmingDelete ? `Bekreft sletting av ${statusLabel}` : `Slett ${statusLabel}`}
                              >
                                {confirmingDelete ? 'Bekreft' : <TbTrash aria-hidden="true" focusable="false" />}
                              </button>
                            </div>
                          </header>

                          {isStatusExpanded ? (
                            <div className="products-form-grid inquiry-attribute-fields">
                              <label className="form-field">
                                <span>Visningsnavn</span>
                                <input
                                  className="input"
                                  type="text"
                                  value={status.label}
                                  onChange={(event) => updateJourneyStatus(index, { label: event.target.value })}
                                  disabled={!canEdit || saving}
                                />
                              </label>
                              <label className="form-field">
                                <span>Farge</span>
                                <select
                                  className="select"
                                  value={status.color}
                                  onChange={(event) => updateJourneyStatus(index, { color: event.target.value })}
                                  disabled={!canEdit || saving}
                                >
                                  {CUSTOMER_JOURNEY_TASK_STATE_COLOR_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="form-field products-form-grid-wide">
                                <span>Vis kundeløpstatus</span>
                                <span className="products-checkbox-line">
                                  <input
                                    type="checkbox"
                                    checked={status.showProgressionStatus !== false}
                                    onChange={(event) => updateJourneyStatus(index, { showProgressionStatus: event.target.checked })}
                                    disabled={!canEdit || saving}
                                  />
                                  <span>Vis dynamisk status som støtte under sluttstatusen</span>
                                </span>
                              </label>
                            </div>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </main>

          <aside className="product-editor-side">
            <section className="product-editor-side-section">
              <h2>Bot-tilgang</h2>
              <div className="bot-assignment-list">
                {bots.length === 0 ? (
                  <p>Ingen boter er tilgjengelige.</p>
                ) : bots.map((bot) => {
                  const assigned = (draft.assignedBotIds || []).map(Number).includes(bot.id);
                  return (
                    <button
                      key={bot.id}
                      type="button"
                      className={`bot-assignment-pill${assigned ? ' is-selected' : ''}`}
                      onClick={() => toggleAssignedBot(bot.id)}
                      disabled={!canEdit || saving}
                      aria-pressed={assigned}
                    >
                      <span className="bot-assignment-pill-main">
                        <strong>{bot.name}</strong>
                        <small>{bot.status || 'active'}</small>
                      </span>
                      <span className="bot-assignment-pill-state">{assigned ? 'Tildelt' : 'Ikke tildelt'}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="product-editor-side-section">
              <h2>Varsling</h2>
              <label className="products-toggle-row">
                <input
                  type="checkbox"
                  checked={draft.notificationSettings?.enabled === true}
                  onChange={(event) => updateDraft({
                    notificationSettings: {
                      ...draft.notificationSettings,
                      enabled: event.target.checked,
                    },
                  })}
                  disabled={!canEdit || saving}
                />
                <span>Send e-post ved nye henvendelser på dette produktet</span>
              </label>
              <div className="label">
                <span className="label-text">E-postmottakere</span>
                <SelectOptionsEditor
                  options={draft.notificationSettings?.emailDestinations || []}
                  onChange={(nextEmails) => updateDraft({
                    notificationSettings: {
                      ...draft.notificationSettings,
                      emailDestinations: nextEmails,
                      emailDestination: nextEmails[0] || '',
                    },
                  })}
                  disabled={!canEdit || saving || draft.notificationSettings?.enabled !== true}
                  addButtonLabel="Legg til e-post"
                  inputLabel="Ny e-post"
                  editLabel="Rediger e-post"
                />
              </div>
            </section>
          </aside>
        </div>
      )}

      {hasUnsavedChanges && (
        <div className="product-editor-save-prompt" role="status" aria-live="polite">
          <div>
            <strong>Ulagrede endringer</strong>
            <p>Endringene forkastes hvis du forlater siden.</p>
          </div>
          <div className="product-editor-save-prompt-actions">
            <button
              type="button"
              className="button button-secondary button-small"
              onClick={discardChanges}
              disabled={saving}
            >
              Forkast
            </button>
            <button
              type="button"
              className="button button-primary button-small"
              onClick={handleSave}
              disabled={!canEdit || saving || loading || !editorAvailable}
            >
              {saving ? 'Lagrer…' : 'Lagre endringer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
